import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef, EventEmitter, Inject,
  Input, OnDestroy, OnInit, Output
} from "@angular/core";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitScheduleDayModel} from "../../../models/visit-schedule-day.model";
import {VisitPlanRouteModel} from "../../../models/visit-plan-route.model";
import {VisitPlanCellModel} from "../../../models/visit-plan-cell.model";
import {
  VisitPlanScheduleResourceSelectListComponent
} from "../visit-plan-schedule-resource-select-list/visit-plan-schedule-resource-select-list.component";
import {PanelService} from "../../../../commons/angular/services/panel.service";

require('./visit-plan-panels.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-panels',
  templateUrl: './visit-plan-panels.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanPanelsComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() index: number;
  @Input() panel: any;
  @Input() panels: any[];
  @Input() visitPlan: VisitPlanModel;
  @Input() visitPlanRange: {dateStart, dateEnd};
  @Input() jobs: any[];
  @Input() routes: VisitPlanRouteModel[];
  @Input() isLoadingRoutes: boolean;
  @Input() agentColorMap: { [key: string]: string };
  @Input() programTypeAliasMap: { [key: string]: string };
  @Input() selectedJobIds: string[];
  @Input() selectedRouteIds: string[];
  @Input() schedulingProjectDays: VisitScheduleDayModel[];
  @Input() visitScheduleDays: VisitScheduleDayModel[];
  @Input() isReloadData: boolean;
  @Output() onClose = new EventEmitter<number>();
  @Output() onCreateRoute = new EventEmitter<{ jobIds: string[], resourceId: string }>();
  @Output() onPanelChange = new EventEmitter<{ panel: any }>();
  @Output() onChangeJobsSelectedIds = new EventEmitter<string[]>();
  @Output() onMoveJobs = new EventEmitter();
  @Output() onSelectedRoutesChange = new EventEmitter<string[]>();
  @Output() onDeleteRoutes = new EventEmitter<string[]>();
  @Output() onReoptimizeRoutes = new EventEmitter<string[]>();
  @Output() onChangeDateRange = new EventEmitter<number>();
  @Output() onLockJobChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onLockRouteChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onRoutePanelContextMenu = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onEditCallServiceTime = new EventEmitter<{ callId: string, slot: VisitPlanCellModel, isDayDispatched: boolean, event: MouseEvent }>();


  public isResizing: boolean;
  public startX = 0;
  public startWidth = 0;

  public onMouseMoveBound!: (event: MouseEvent | TouchEvent) => void;
  public onMouseUpBound!: (event: MouseEvent | TouchEvent) => void;
  public onWindowResizeBound: any;

  constructor(@Inject(ElementRef) private elRef: ElementRef,
              @Inject(PanelService) private panelService: PanelService,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {
  }

  // public selectedJobIds: string[] = [];

  ngOnInit() {
    // Bind context for event handlers
    this.onMouseMoveBound = this.onMouseMove.bind(this);
    this.onMouseUpBound = this.onMouseUp.bind(this);

    // Add window resize listener
    this.onWindowResizeBound = this.setPanelWidth.bind(this);
    window.addEventListener('resize', this.onWindowResizeBound);

    // Initial width check (wait until next tick to ensure rendering)
    // setTimeout(() => this.setTotalsWidth(), 0);

    // Add document mouseup/touchend listeners
    document.addEventListener('mouseup', this.onMouseUpBound);
    document.addEventListener('touchend', this.onMouseUpBound);
  }

  ngAfterViewInit(): void {
    const handle = this.elRef.nativeElement.querySelector('.resize-handle');
    if (!handle) return;

    handle.addEventListener('mousedown', this.startResize.bind(this));
    handle.addEventListener('touchstart', this.startResize.bind(this), {
      passive: false,
    });
  }

  ngOnDestroy() {
    document.removeEventListener('mousemove', this.onMouseMoveBound);
    document.removeEventListener('touchmove', this.onMouseMoveBound);
    document.removeEventListener('mouseup', this.onMouseUpBound);
    document.removeEventListener('touchend', this.onMouseUpBound);
    window.removeEventListener('resize', this.onWindowResizeBound);
  }


  // onChangeJobsSelectedIds(selectedIds: string[]) {
  //   this.selectedJobIds = [...selectedIds];
  // }

  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();

    const handle = event.target as HTMLElement;
    handle.classList.add('active');

    this.isResizing = true;

    const e = event instanceof TouchEvent ? event.touches[0] : event;
    this.startX = e.clientX;

    const panel = this.elRef.nativeElement.querySelector('.panel-container') as HTMLElement;
    if (!panel) return;
    this.startWidth = panel.offsetWidth;

    document.addEventListener('mousemove', this.onMouseMoveBound);
    document.addEventListener('touchmove', this.onMouseMoveBound);
  }

  onMouseMove(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing) return;

    const e = event instanceof TouchEvent ? event.touches[0] : event;
    const dx = this.startX - e.clientX;
    const newWidth = this.startWidth + dx;

    if (newWidth >= 200) {
      this.setPanelWidth(newWidth);
    }
  }

  onMouseUp(): void {
    if (!this.isResizing) return;

    const handle = this.elRef.nativeElement.querySelector('.resize-handle');
    handle?.classList.remove('active');

    this.isResizing = false;

    document.removeEventListener('mousemove', this.onMouseMoveBound);
    document.removeEventListener('touchmove', this.onMouseMoveBound);

    // Save the final width from one of the sticky elements
    // const sticky = this.elRef.nativeElement.querySelector('.sticky') as HTMLElement;
  }

  setPanelWidth(width?: number): void {
    const panel = this.elRef.nativeElement.querySelector('.panel-container') as HTMLElement;
    panel.style.width = `${width}px`;
    panel.style.maxWidth = `${width}px`;
  }

  onAssign(event, jobIds: string[], visitPlan: VisitPlanModel, dateRange?: { from: number, to: number }) {
    // A deleted (archived) project is read only.
    // The resource select list imports external resource holders on its own, so it must not be opened at all.
    if (visitPlan?.hasNotSyncedChanges || visitPlan?.isArchive) {
      return;
    }

    const panel = this.panelService.open(VisitPlanScheduleResourceSelectListComponent, event, {
      visitPlan: visitPlan,
      visitScheduleId: visitPlan.scheduleId,
      dateRange,
      jobIds: jobIds,
    }, {
      width: 600,
      height: 370
    })

    if (panel) {
      panel.afterClose$.subscribe((data) => {
        if (!!data?.selectedResourceId) {
          this.onCreateRoute.emit({jobIds: this.selectedJobIds, resourceId: data.selectedResourceId});
          this.selectedJobIds = [];
          this.cdr.detectChanges();
        }
      });
    }
  }

}
